import React from "react";
import { toast } from "sonner";
import { BarChart3, User, Phone, AlertCircle, Building2, TrendingUp, RefreshCw } from "lucide-react";
import { adminApi } from "../lib/api";

export default function DriverPerformancePanel() {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(30);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get(`/admin/drivers-performance?days=${days}`);
      setData(data);
    } catch { toast.error("Kunne ikke hente performance-data"); }
    finally { setLoading(false); }
  }, [days]);

  React.useEffect(() => { load(); }, [load]);

  const scoreColor = (c) => c === "green" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : c === "yellow" ? "text-amber-800 bg-amber-50 border-amber-200"
    : c === "red" ? "text-rose-800 bg-rose-50 border-rose-200"
    : "text-slate-600 bg-slate-50 border-slate-200";
  const ringColor = (c) => c === "green" ? "#10b981" : c === "yellow" ? "#f59e0b" : c === "red" ? "#e11d48" : "#94a3b8";

  if (!data) return <div className="text-brand-text2 p-6" data-testid="admin-perf-loading">Indlæser...</div>;

  const summary = {
    total: data.count,
    topPerformers: data.drivers.filter(d => d.color === "green").length,
    needsTraining: data.drivers.filter(d => d.color === "red" || d.fail_rate > 0.05).length,
    avgScore: (() => {
      const scored = data.drivers.filter(d => d.score !== null);
      return scored.length ? (scored.reduce((s, d) => s + d.score, 0) / scored.length) : 0;
    })(),
  };

  return (
    <div className="space-y-4" data-testid="admin-perf-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="kicker">Chauffør-Performance</div>
          <h2 className="font-display font-bold text-2xl text-brand-dark mt-1">Overblik over alle chauffører</h2>
          <p className="text-sm text-brand-text2 mt-1">Score 0-100 baseret på til-tiden, forgæves-rate og opkalds-compliance.</p>
        </div>
        <div className="flex gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="input-flat" data-testid="admin-perf-period">
            <option value={7}>7 dage</option>
            <option value={30}>30 dage</option>
            <option value={90}>90 dage</option>
            <option value={365}>12 måneder</option>
          </select>
          <button onClick={load} disabled={loading} className="btn-secondary inline-flex items-center gap-2"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Opdatér</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-brand p-4"><div className="text-xs uppercase tracking-widest text-brand-text2">Total</div><div className="font-display font-bold text-2xl text-brand-dark mt-1">{summary.total}</div></div>
        <div className="bg-white border border-emerald-300 p-4"><div className="text-xs uppercase tracking-widest text-emerald-700">Topperformere</div><div className="font-display font-bold text-2xl text-emerald-700 mt-1">{summary.topPerformers}</div></div>
        <div className="bg-white border border-rose-300 p-4"><div className="text-xs uppercase tracking-widest text-rose-700">Skal forbedres</div><div className="font-display font-bold text-2xl text-rose-700 mt-1">{summary.needsTraining}</div></div>
        <div className="bg-white border border-brand p-4"><div className="text-xs uppercase tracking-widest text-brand-text2">Gns. score</div><div className="font-display font-bold text-2xl text-brand-dark mt-1">{summary.avgScore.toFixed(1)}</div></div>
      </div>

      {data.drivers.length === 0 ? (
        <div className="bg-white border border-brand p-10 text-center" data-testid="admin-perf-empty">
          <BarChart3 size={32} className="mx-auto text-brand-text2" />
          <div className="font-display font-semibold text-brand-dark mt-3">Ingen aktive chauffører</div>
        </div>
      ) : (
        <div className="bg-white border border-brand overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase tracking-widest text-brand-text2">
              <tr>
                <th className="px-4 py-3 text-left">Chauffør</th>
                <th className="px-3 py-3 text-left">Vognmand</th>
                <th className="px-3 py-3 text-center">Score</th>
                <th className="px-3 py-3 text-right">Leverancer</th>
                <th className="px-3 py-3 text-right">Til tiden</th>
                <th className="px-3 py-3 text-right">Forgæves</th>
                <th className="px-3 py-3 text-right">Opkald</th>
                <th className="px-3 py-3 text-right">Gns. tid</th>
              </tr>
            </thead>
            <tbody>
              {data.drivers.map((p) => (
                <tr key={p.driver_id} className="border-t border-brand/30 hover:bg-brand-bg2" data-testid={`admin-perf-row-${p.driver_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-brand-dark flex items-center gap-2"><User size={12} className="text-brand-red" />{p.driver.name}</div>
                    <div className="text-xs text-brand-text2">{p.driver.email}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-brand-text2">
                    {p.subcontractor ? (
                      <div className="inline-flex items-center gap-1"><Building2 size={11} />{p.subcontractor.company_name}</div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="relative w-12 h-12">
                        <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3.5" />
                          {p.score !== null && (
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke={ringColor(p.color)} strokeWidth="3.5"
                                    strokeDasharray={`${p.score}, 100`} strokeLinecap="round" />
                          )}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center font-display font-bold text-sm text-brand-dark">{p.score !== null ? p.score.toFixed(0) : "—"}</div>
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 border ${scoreColor(p.color)}`}>
                        {p.color === "green" ? "Top" : p.color === "yellow" ? "OK" : p.color === "red" ? "Lav" : "Ny"}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono">{p.delivered}/{p.delivered + p.failed}</td>
                  <td className="px-3 py-3 text-right font-mono">{(p.on_time_rate * 100).toFixed(0)}%</td>
                  <td className={`px-3 py-3 text-right font-mono ${p.fail_rate > 0.05 ? "text-rose-700 font-bold" : ""}`}>
                    {(p.fail_rate * 100).toFixed(1)}% {p.fail_rate > 0.05 && <TrendingUp size={11} className="inline" />}
                  </td>
                  <td className="px-3 py-3 text-right font-mono">
                    {p.no_home_failed > 0 ? `${(p.call_compliance_rate * 100).toFixed(0)}%` : "—"}
                  </td>
                  <td className="px-3 py-3 text-right font-mono">{p.avg_delivery_minutes ? `${p.avg_delivery_minutes.toFixed(0)} min` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-xs text-brand-text2 italic">
        <AlertCircle size={12} className="inline mr-1" /> Chauffører med {">"}5% forgæves eller manglende opkald udløser automatisk træning i Fase 3 (under udvikling).
      </div>
    </div>
  );
}
